import React, { useCallback, useState } from 'react';
import { Button } from 'reactstrap';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import {
  individual_column_style,
  individual_header_style,
  icon_column_style,
  individual_icon_header_style
} from '../../../../../../../../assets/styles/table_custom_style';
import BootstrapTable from 'react-bootstrap-table-next';
import { useHistory } from 'react-router-dom';
import { toBrazilianFullDate } from 'views/CRM/shared/utils/date';
import ProductService from '../../../../../services/productService';
import { toast } from 'react-toastify';
import ModalConfirmation from 'components/Modal/ConfirmationModal';

import './styles.css';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import { maskPhoneNumber } from 'views/CRM/shared/utils/phoneNumberHelpers';
import { maskCpf } from 'views/CRM/shared/utils/cpfCnpjHelpers';
import { maskCnpj } from 'views/CRM/shared/utils/cpfCnpjHelpers';
import { verifyRoles } from 'components/Authentication/VerifyRoles';

const ListProductsTable = ({ products, reloadProducts, customFields }) => {
  const [openModalConfirmation, setOpenModalConfirmation] = useState(false);
  const [productId, setProductId] = useState(null);
  const history = useHistory();

  const handleDeleteProduct = useCallback(
    (id) => {
      ProductService.deleteProduct(id)
        .catch((err) => {
          const message =
            err?.response?.data?.message || 'Produto deletado com sucesso';
          toast.error(message);
        })
        .finally(() => {
          setOpenModalConfirmation(false);
          reloadProducts();
        });
    },
    [reloadProducts]
  );

  const navigateToEditProductPage = (product) => {
    history.push(
      `/admin/crm/product-details/${product.recordTypeId}/${product.id}`
    );
  };

  const formatCurrency = (value) => {
    return `R$ ${parseFloat(value).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const handleEnableDisableCustomerPortfolio = async (id, status) => {
    await ProductService.changeProductStatus(id, status)
      .then(() => {
        toast.success('Produto habilitado/desabilitado com sucesso!', {
          autoClose: 3000,
          closeOnClick: true
        });
      })
      .catch((error) => {
        const msg = error?.response?.data?.message;
        toast.error(msg || 'Erro ao habilitado/desabilitado o Produto', {
          autoClose: 3000,
          closeOnClick: true
        });
      });
  };

  const toggleProductStatus = useCallback(
    ({ id, isActive }) => {
      try {
        handleEnableDisableCustomerPortfolio(
          id,
          isActive ? 'disable' : 'enable'
        );
      } catch (error) {
        const msg = error?.response?.data?.message;
        toast.error(
          msg || `Erro ao ${isActive ? 'desabilitar' : 'habilitar'} Produto`
        );
      }
      setTimeout(() => {
        reloadProducts();
      }, 300);
    },
    [reloadProducts]
  );

  const createDynamicColumns = () => {
    const columns = [];

    columns.push(
      {
        dataField: 'name',
        text: 'Nome',
        sort: true,
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_, row) => {
          return (
            <>
              {verifyRoles(['CRM_PRODUCTS_EDIT']) ? (
                <span
                  className="product-name"
                  onClick={() => {
                    navigateToEditProductPage(row);
                  }}
                >
                  {row.name}
                </span>
              ) : (
                <span>{row.name}</span>
              )}
            </>
          );
        }
      },
      {
        dataField: 'code',
        text: 'Código',
        style: individual_column_style
      },
      {
        dataField: 'basePrice',
        text: 'Valor Base',
        style: individual_column_style,
        formatter: (cell) => formatCurrency(cell)
      },
      {
        dataField: 'externalId',
        text: 'ID Externo',
        style: individual_column_style
      },
      {
        dataField: 'createdAt',
        text: 'Criado em',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (cell) => {
          if (cell) return toBrazilianFullDate(cell);
        }
      },
      {
        dataField: 'updatedAt',
        text: 'Última atualização',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (cell) => {
          if (cell) return toBrazilianFullDate(cell);
        }
      }
    );

    (customFields || []).forEach((field) => {
      columns.push({
        dataField: field.name,
        text: field.label,
        style: individual_column_style,
        formatter: (_, row) => {
          if (field.type === 'DATE' && !!row[field.name]) {
            return new Date(row[field.name]).toLocaleDateString('pt-br');
          } else if (field.type === 'TELEPHONE' && row[field.name]) {
            return maskPhoneNumber(row[field.name]);
          } else if (field.type === 'CPF' && row[field.name]) {
            return maskCpf(row[field.name]);
          } else if (field.type === 'CNPJ' && row[field.name]) {
            return maskCnpj(row[field.name]);
          } else if (field.type === 'CURRENCY' && row[field.name]) {
            return formatCurrency(row[field.name]);
          }
          return row[`${field.name}`];
        }
      });
    });

    columns.push(
      {
        dataField: 'edit',
        text: 'Editar',
        style: icon_column_style,
        headerStyle: individual_icon_header_style,
        formatter: (_, row) => {
          return (
            <>
              {verifyRoles(['CRM_PRODUCTS_EDIT']) ? (
                <Button
                  size="sm"
                  color="info"
                  onClick={() => navigateToEditProductPage(row)}
                >
                  <li className="fas fa-user-edit"></li>
                </Button>
              ) : null}
            </>
          );
        }
      },
      {
        dataField: 'habilitar/desabilitar',
        style: icon_column_style,
        headerStyle: individual_icon_header_style,
        text: 'Habilitar/Desabilitar',
        // eslint-disable-next-line react/display-name
        formatter: (_, row) => {
          return (
            <>
              {verifyRoles(['CRM_PRODUCTS_TOGGLE']) ? (
                <ToggleSwitch
                  checked={row?.isActive}
                  id={row.id}
                  onChange={() =>
                    toggleProductStatus({
                      id: row.id,
                      isActive: row?.isActive
                    })
                  }
                />
              ) : null}
            </>
          );
        }
      }
    );
    return [...columns];
  };

  const dynamicFunctionColumns = createDynamicColumns();

  return (
    <>
      <ToolkitProvider
        data={products || []}
        keyField="id"
        columns={dynamicFunctionColumns}
      >
        {(props) => (
          <div className="py-4 table-responsive">
            <BootstrapTable
              {...props.baseProps}
              bootstrap4={true}
              bordered={false}
              classes="align-items-center"
            />
          </div>
        )}
      </ToolkitProvider>

      <ModalConfirmation
        isModalOpen={openModalConfirmation}
        onModalToggle={setOpenModalConfirmation}
        unmountOnClose={true}
        idResource={productId}
        deleteResource={handleDeleteProduct}
        message={
          'Tem certeza que deseja excluir esta conta? Todas as oportunidades e contatos relacionados a ela serão juntamente excluídas.'
        }
      />
    </>
  );
};

export default ListProductsTable;
